import React, { useMemo } from "react"
import { get } from "lodash"
import {
  Box,
  useTheme,
  Slide,
  SlideProps,
  Typography,
  Avatar,
} from "@mui/material"
import { BaseModal, IBaseModalProps } from "components/modal"
import { useTranslation } from "providers/i18n"
import { getIcon } from "utils/icon"
import { ProductData } from "providers/venue/types"
import { StoreIcon } from "components/icons"
import { IFeatureDetailResult } from "hooks/feature"
import { IOccupantDetail } from "utils/feature"

import { useMobileUrl } from "hooks/useMobileUrl"
import { QRCode } from "components/QRCode"
import { ProductCard } from "./ProductCard"

const ModalTransition = React.forwardRef<HTMLElement, SlideProps>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
  }
)

export interface IProductModalProps extends Omit<IBaseModalProps, "children"> {
  data: Partial<IFeatureDetailResult>
}

export const ProductModal: React.FC<IProductModalProps> = ({
  data = {},
  sx = {},
  onClose,
  open = true,
  ...props
}) => {
  const { mobileUrl } = useMobileUrl()
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    root,
    productContainer,
    productCard,
    shopContainer,
    shopInfoContainer,
    avatar,
    label,
    qrCodeContainer,
    qrCode,
    qrCodeCaptionContainer,
    qrcodePrimaryText,
    qrcodeSecondaryText,
  } = theme.components?.[`ProductModal`].styleOverrides || {}

  const { name, logo } = data as IOccupantDetail

  const products = useMemo(
    () => get(data, "products", []) as ProductData[],
    [data]
  )

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      sx={sx}
      {...props}
      TransitionComponent={ModalTransition}
    >
      <Box sx={root}>
        <Box sx={productContainer}>
          {products.map((product, index) => {
            const coverImage = get(product, "properties.cover_image")
            const name = get(product, "properties.name")
            return (
              <ProductCard
                sx={productCard}
                data={{ name, coverImage }}
                key={`product-${product?.id}`}
              />
            )
          })}
        </Box>
        <Box sx={shopContainer}>
          <Box sx={shopInfoContainer}>
            <Avatar sx={avatar} src={logo}>
              {getIcon(data.categoryIconProp, null) || <StoreIcon />}
            </Avatar>
            <Typography sx={label} gutterBottom variant="h5">
              {t(name)}
            </Typography>
          </Box>
          {mobileUrl && (
            <>
              <Box sx={qrCodeContainer}>
                <Box sx={qrCodeCaptionContainer}>
                  <Typography
                    variant="subtitle2"
                    component="p"
                    sx={qrcodePrimaryText}
                  >
                    {t("Scan here for")}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={qrcodeSecondaryText}
                  >
                    {t("Recommended Menu")}
                  </Typography>
                </Box>
                <QRCode sx={qrCode} url={mobileUrl} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </BaseModal>
  )
}
